import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';

export type BottomSheetAnchor = 'top' | 'half' | 'bottom';

@Component({
    selector: 'tt-bottom-sheet',
    templateUrl: './bottom-sheet.component.html',
    styleUrls: ['./bottom-sheet.component.css'],
})
export class BottomSheetComponent implements OnInit, OnChanges, OnDestroy {
    /**
     * Whether the top anchor of the bottom sheet should be active or not.
     *
     * @default true
     */
    @Input()
    public set ttTopAnchorActive(value: BooleanInput) {
        this._topAnchorActive = coerceBooleanProperty(value);
    }
    public get ttTopAnchorActive(): boolean {
        return this._topAnchorActive;
    }
    private _topAnchorActive = true;

    /**
     * Whether the middle anchor of the bottom sheet should be active or not. Without it the sheet can only fully expand or be at the bottom.
     *
     * @default true
     */
    @Input()
    public set ttHalfAnchorActive(value: BooleanInput) {
        this._halfAnchorActive = coerceBooleanProperty(value);
    }
    public get ttHalfAnchorActive(): boolean {
        return this._halfAnchorActive;
    }
    private _halfAnchorActive = true;

    /**
     * Whether the bottom anchor of the bottom sheet should be active or not.
     *
     * @default true
     */
    @Input()
    public set ttBottomAnchorActive(value: BooleanInput) {
        this._bottomAnchorActive = coerceBooleanProperty(value);
    }
    public get ttBottomAnchorActive(): boolean {
        return this._bottomAnchorActive;
    }
    private _bottomAnchorActive = true;

    /**
     * The minimum height of the bottom sheet, use size notation, not pure number.
     */
    @Input()
    public ttMinHeight: string = '30rem';

    /**
     * Reference to view element of the the container of the bottom sheet (parent to content).
     */
    @ViewChild('containerSheet')
    public containerSheet?: ElementRef<HTMLDivElement>;

    /**
     * Reference to the view element wrapped around the content-slot of the bottom sheet.
     */
    @ViewChild('contentDiv')
    public contentDiv?: ElementRef<HTMLDivElement>;

    /**
     * The active anchor state of the bottom sheet.
     */
    public anchorState: BottomSheetAnchor = 'bottom';

    /**
     * The pixels between the touch y coordinate and the start position of the sheet.
     */
    private dragOffsetYPixels = '';

    /**
     * The start y coordinate of the touch start event.
     */
    private dragStartY: number = 0;

    /**
     * The time when the touch start event happened.
     */
    private dragStartTime: number = 0;

    private activeMousePointerId?: number;

    /**
     * The scrolltop of the content element when touch event starts.
     */
    private startScrollTop: number = 0;

    /**
     * The style applied ot the outer sheet, for animating drag.
     */
    public outerSheetStyle: Partial<CSSStyleDeclaration> = {};

    constructor(private elementRef: ElementRef) {}

    public onTouchStart(event: TouchEvent) {
        this.startDrag(event.targetTouches[0]?.clientY);
    }

    public onTouchMove(event: TouchEvent) {
        this.moveDrag(event.targetTouches[0]?.clientY, event);
    }

    public onTouchEnd(event: TouchEvent) {
        this.endDrag(event.changedTouches[0]?.clientY);
    }

    public onMousePointerDown(event: PointerEvent): void {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;

        this.activeMousePointerId = event.pointerId;
        (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
        this.startDrag(event.clientY);
    }

    public onMousePointerMove(event: PointerEvent): void {
        if (event.pointerType !== 'mouse' || event.pointerId !== this.activeMousePointerId || event.buttons !== 1) return;

        this.moveDrag(event.clientY, event);
    }

    public onMousePointerUp(event: PointerEvent): void {
        if (event.pointerType !== 'mouse' || event.pointerId !== this.activeMousePointerId) return;

        this.activeMousePointerId = undefined;
        const target = event.currentTarget as HTMLElement;
        if (target.hasPointerCapture?.(event.pointerId)) target.releasePointerCapture(event.pointerId);
        this.endDrag(event.clientY);
    }

    public onMousePointerCancel(event: PointerEvent): void {
        if (event.pointerType === 'mouse' && event.pointerId === this.activeMousePointerId) this.activeMousePointerId = undefined;
    }

    private startDrag(clientY: number | undefined): void {
        if (!this.containerSheet || !this.contentDiv) return;

        if (!this.isValidNumber(clientY)) return;

        this.dragStartY = clientY;
        this.dragStartTime = Date.now();

        const sheetStartPositionY = this.containerSheet.nativeElement.getBoundingClientRect().top;
        this.startScrollTop = this.contentDiv.nativeElement.scrollTop;

        if (this.isValidNumber(sheetStartPositionY)) {
            this.dragOffsetYPixels = `${this.dragStartY - sheetStartPositionY}px`;
        }
    }

    private moveDrag(clientY: number | undefined, event: Event): void {
        if (!this.isValidNumber(clientY)) return;

        if (this.anchorState !== 'top' || this.anchoredToTopAndSwipingDownWithoutScroll(clientY)) {
            event.preventDefault();
            this.outerSheetStyle.transform = `translateY(calc(${clientY}px - ${this.dragOffsetYPixels}))`;
        }
    }

    private endDrag(clientY: number | undefined): void {
        if (!this.isValidNumber(clientY)) return;

        const dragLength = Math.abs(this.dragStartY - clientY);

        if (this.startScrollTop !== 0 || dragLength < 15) return;

        if (this.shouldSnapToTopAnchor(clientY) && this.ttTopAnchorActive) {
            this.snapToAnchor('top');
        } else if (this.shouldSnapToHalfAnchor(clientY)) {
            this.snapToAnchor('half');
        } else {
            this.snapToAnchor('bottom');
        }
    }

    /**
     * Closes the bottom sheet (anchoring to bottom).
     */
    public closeExpandedSheet() {
        this.snapToAnchor('bottom');
    }

    private shouldSnapToTopAnchor(dragEndY: number) {
        if (this.swipedUp(dragEndY) && (this.swipedFast(dragEndY) || this.isPointerOnTopHalfOfScreen(dragEndY) || this.isSheetOnTopHalfOfScreen())) {
            return true;
        }

        return false;
    }

    private shouldSnapToHalfAnchor(dragEndY: number) {
        if (this.slowSwipeUpOnBottomHalfOfScreen(dragEndY) || this.swipeDownOnActiveTopAnchor(dragEndY)) {
            return true;
        }

        return false;
    }

    /**
     * Snaps the bottom sheet to the given anchor point.
     *
     * @param anchor the anchor point to snap the bottom-sheet to.
     */
    private snapToAnchor(anchor: BottomSheetAnchor) {
        this.outerSheetStyle.transition = 'all 0.1s ease-in-out';

        setTimeout(() => {
            let transform = this.outerSheetStyle.transform;

            if (anchor === 'top' && this.ttTopAnchorActive) {
                transform = `translateY(5rem)`;
                this.anchorState = 'top';
            } else if (anchor === 'half' && this.ttHalfAnchorActive) {
                transform = `translateY(calc(50% - 5rem))`;
                this.anchorState = 'half';
            } else if (anchor === 'bottom' && this.ttBottomAnchorActive) {
                transform = `translateY(calc(100% - ${this.ttMinHeight}))`;
                this.anchorState = 'bottom';
            }

            this.outerSheetStyle.transform = transform;
            this.outerSheetStyle.transition = 'none';
        }, 5);
    }

    private isSheetOnTopHalfOfScreen() {
        return !!this.containerSheet && this.containerSheet.nativeElement.getBoundingClientRect().top < window.innerHeight / 2;
    }

    private swipeDownOnActiveTopAnchor(touchEndY: number) {
        return !this.swipedUp(touchEndY) && this.anchorState === 'top';
    }

    private slowSwipeUpOnBottomHalfOfScreen(dragEndY: number): boolean {
        return this.swipedUp(dragEndY) && !this.swipedFast(dragEndY) && !this.isPointerOnTopHalfOfScreen(dragEndY);
    }

    private anchoredToTopAndSwipingDownWithoutScroll(dragY: number): boolean {
        return this.anchorState === 'top' && this.startScrollTop === 0 && !this.swipedUp(dragY);
    }

    private isValidNumber(value: unknown): value is number {
        return value === 0 || (!!value && !isNaN(+value));
    }

    /**
     * Checks whether the given y coordinate indicates that the user swiped or not.
     *
     * @param touchEndY the y coordinate to get the direction of the swipe from.
     * @returns `true` if the user swiped up, `false` if not.
     */
    private swipedUp(touchEndY: number) {
        if (touchEndY < this.dragStartY) {
            return true;
        }
        return false;
    }

    private swipedFast(touchEndY: number) {
        const touchLengthThreshold = 100;
        const touchLength = Math.abs(this.dragStartY - touchEndY);

        const touchTimeYThreshold = 150;
        const touchTimeY = Date.now() - this.dragStartTime;

        if (touchTimeY < touchTimeYThreshold && touchLength > touchLengthThreshold) {
            return true;
        }

        return false;
    }

    private isPointerOnTopHalfOfScreen(touchY: number) {
        const navbarHeight = document.querySelector('.tt-navbar')?.clientHeight || 0;

        return touchY - navbarHeight < (window.innerHeight - navbarHeight) / 2;
    }

    ngOnInit(): void {
        document.body.appendChild(this.elementRef.nativeElement);
        const uiContainer = document.querySelector('.cf')?.querySelector('div');
        this.outerSheetStyle.transform = `translateY(calc(100% - ${this.ttMinHeight}))`;

        if (uiContainer) {
            uiContainer.style.paddingBottom = this.ttMinHeight;
        }

        this.anchorState = 'bottom';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttTopAnchorActive']) {
            this.ttTopAnchorActive = changes['ttTopAnchorActive'].currentValue;
        }

        if (changes['ttHalfAnchorActive']) {
            this.ttHalfAnchorActive = changes['ttHalfAnchorActive'].currentValue;
        }

        if (changes['ttBottomAnchorActive']) {
            this.ttBottomAnchorActive = changes['ttBottomAnchorActive'].currentValue;
        }
    }

    ngOnDestroy(): void {
        document.body.removeChild(this.elementRef.nativeElement);
        const uiContainer = document.querySelector('.cf')?.querySelector('div');

        if (uiContainer) {
            uiContainer.style.paddingBottom = '0';
        }
    }
}
